// Prices, dates and small text helpers for the public site. The price rules
// match functions/_lib/price.js and functions/_lib/geo/money.js.

// Prices are stored either in rupees (50000000) or in the project's unit
// (5 with "Cr"); both occur, so a number that would be absurd in the unit is
// read as rupees.
const PRICE_UNITS = { cr: 1e7, crore: 1e7, crores: 1e7, l: 1e5, lac: 1e5, lakh: 1e5, lakhs: 1e5, lacs: 1e5 };
export function toRupees(value, label) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const m = PRICE_UNITS[String(label || '').trim().toLowerCase().replace(/\.$/, '')] || 1;
  return m > 1 && n >= 1e5 ? n : n * m;
}

// ₹ in the Indian way: 5 Cr, 45 L, 85,000.
export function formatINR(rupees) {
  if (rupees === null || rupees === undefined) return '';
  const r = Number(rupees);
  const short = (v) => String(Number(v.toFixed(2)));
  if (r >= 1e7) return `₹${short(r / 1e7)} Cr`;
  if (r >= 1e5) return `₹${short(r / 1e5)} L`;
  return `₹${Math.round(r).toLocaleString('en-IN')}`;
}

const CURRENCY_SYMBOLS = { USD: '$', GBP: '£', EUR: '€', CAD: 'CA$', AUD: 'A$', SGD: 'S$', JPY: '¥', CNY: 'CN¥' };
const MONEY_UNITS = { k: 1e3, thousand: 1e3, m: 1e6, mn: 1e6, million: 1e6, b: 1e9, bn: 1e9, billion: 1e9 };
export function toAmount(value, label) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const m = MONEY_UNITS[String(label || '').trim().toLowerCase().replace(/\.$/, '')] || 1;
  return m > 1 && n >= 1e5 ? n : n * m;
}
export function formatMoney(amount, currency) {
  const n = Number(amount);
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const sep = /^[A-Z]{2,3}$/.test(symbol) ? ' ' : '';
  const short = (v) => String(Number(v.toFixed(2)));
  if (n >= 1e9) return `${symbol}${sep}${short(n / 1e9)}B`;
  if (n >= 1e6) return `${symbol}${sep}${short(n / 1e6)}M`;
  if (n >= 1e4) return `${symbol}${sep}${short(n / 1e3)}K`;
  return `${symbol}${sep}${Math.round(n).toLocaleString('en-US')}`;
}

// "₹5 Cr – ₹12 Cr", "From ₹50 L", "Price on request".
export function formatPrice(project) {
  const currency = project.currency || 'INR';
  const inr = currency === 'INR';
  const read = inr ? toRupees : toAmount;
  const fmt = inr ? formatINR : (v) => formatMoney(v, currency);
  const min = read(project.price_min, project.price_unit_label);
  const max = read(project.price_max, project.price_unit_label);
  if (min && max) return min === max ? fmt(min) : `${fmt(min)} – ${fmt(max)}`;
  if (min) return `From ${fmt(min)}`;
  if (max) return `Up to ${fmt(max)}`;
  return 'Price on request';
}

// Dates always in the same zone and language, so the server's HTML and the
// browser's agree (a mismatch would make React redraw the page).
export const shortDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};
export const monthYear = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso || '') : d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

export const slugify = (text) => String(text || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-+|-+$)/g, '');

// A YouTube video id from the common URL shapes (watch?v=, youtu.be/, embed/).
export function parseYouTubeId(url) {
  if (!url) return null;
  for (const re of [/(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/, /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/, /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/]) {
    const m = String(url).match(re);
    if (m) return m[1];
  }
  return null;
}

// Plain text with blank-line paragraphs, for blog posts and descriptions.
export const paragraphsOf = (text) => String(text || '').split(/\n\s*\n/).map((t) => t.trim()).filter(Boolean);

// What a tenant typed into a link field must be an http(s) address.
export const safeUrl = (url) => (/^https?:\/\//i.test(String(url || '').trim()) ? String(url).trim() : '');

export const digits = (s) => String(s || '').replace(/[^0-9]/g, '');
export const telHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

// Project pages and cards: where they live.
export const projectHref = (p) => `/project?id=${encodeURIComponent(p.id)}`;
